import { useState, type ReactNode } from "react";
import Close from "@mui/icons-material/Close";
import Info from "@mui/icons-material/Info";
import Warning from "@mui/icons-material/Warning";
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import type { Promisable } from "type-fest";

import { dayjs, NhsNumberSchema } from "@thalamos/common";
import type {
  AvailablePatientListRow,
  PatientListEntry,
  PatientStateWithContext,
} from "@thalamos/aspire-common";
import {
  defaultPatientListSort,
  getActiveMhaStatus,
  MhaStatus,
} from "@thalamos/aspire-common";

import { FilterableColumnHeader } from "./FilterableColumnHeader.js";
import type { FilterOption } from "./FilterableColumnHeader.js";
import { PatientSearchField } from "./PatientSearchField.js";
import {
  SortableColumnHeader,
  type PatientListSort,
} from "./SortableColumnHeader.js";
import {
  Banner,
  BannerList,
  Button,
  ButtonIcon,
  LoadingSpinner,
} from "~/components/design-system/index.js";
import { TooltipInfoBox } from "~/components/design-system/TooltipInfoBox.js";
import { formatMhaStatus } from "~/components/util.js";
import PaginationControls from "~/pages/Caseload/PaginationControls.js";
import { ConfirmationModal } from "~/pages/ConfirmationModal.js";
import {
  classifyExpiry,
  expiryThresholdTimes,
  hospitalOrPosName,
} from "~/pages/MhaStatusDashboard/MhaStatusDashboard.js";
import { routeFns } from "~/routePaths.js";
import { ukLocalFormatDate } from "~/util.js";

import "../../@types/index.js";

const COLUMN_COUNT = 8;

export type PatientListProps = {
  entries: PatientListEntry[];
  total: number;
  page: number;
  rowsPerPage: number;
  isLoading: boolean;
  error?: string;
  sort: PatientListSort;
  onSortChange: (sort: PatientListSort) => void;
  searchTerm: string;
  onSearchChange: (searchTerm: string) => void;
  onClearFilters: () => void;
  availableMhaStatuses: MhaStatus[];
  selectedMhaStatuses: MhaStatus[];
  onMhaStatusesChange: (statuses: MhaStatus[]) => void;
  availableLocationNames: string[];
  selectedLocationNames: string[];
  onLocationNamesChange: (locationNames: string[]) => void;
  onPageChange: (page: number) => void;
  onAddPatient?: () => void;
  onRemovePatient: (rootPatientId: string) => Promisable<void>;
};

// The list writes every date with slashes, including those that carry a time.
function formatListDateTime(utcValue: string) {
  return dayjs.utc(utcValue).tz("Europe/London").format("DD/MM/YYYY HH:mm");
}

type RemovalControl = {
  isRemoving: boolean;
  onRemove: () => Promisable<void>;
};

function RemoveFromListCell({
  patientName,
  isRemoving,
  onRemove,
}: RemovalControl & { patientName: string | null }) {
  return (
    <TableCell padding="checkbox">
      <IconButton
        size="small"
        aria-label={
          patientName === null
            ? "Remove this patient from your patient list"
            : `Remove ${patientName} from your patient list`
        }
        disabled={isRemoving}
        onClick={() => {
          void onRemove();
        }}
      >
        <Close fontSize="small" />
      </IconButton>
    </TableCell>
  );
}

function ExpiryCell({ state }: { state: PatientStateWithContext }) {
  const theme = useTheme();
  const activeState = getActiveMhaStatus(state.state, dayjs().toISOString());
  const status = state.state.mhaStatus.status;
  const expiryDateTime = state.state.mhaStatus.expiryDateTime;

  const classification = classifyExpiry(
    status,
    expiryDateTime,
    dayjs(),
    activeState.type,
  );

  if (classification === "unknown" || !expiryDateTime) {
    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Warning sx={{ color: "orange", marginRight: "0.2em" }} />
        Unknown
      </Box>
    );
  }

  const formatExpiry = expiryThresholdTimes[status]?.showTime
    ? formatListDateTime
    : ukLocalFormatDate;
  const formatted = formatExpiry(expiryDateTime);

  const overridden = state.expiryOverrideInfo;
  const dateWithOverrideHint = (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {formatted}
      {overridden && (
        <TooltipInfoBox
          title={`Expiry time manually overridden from ${formatExpiry(overridden.defaultExpiryDateTime)}`}
          icon={
            <Info
              sx={{
                color: theme.palette.primary.main,
                fontSize: "1.25rem",
                ml: 0.5,
              }}
            />
          }
        />
      )}
    </Box>
  );

  const warningColour: Record<string, string | undefined> = {
    expired: "red",
    critical: "red",
    warning: "orange",
    normal: undefined,
  };
  const colour = warningColour[classification];

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {colour && <Warning sx={{ color: colour, marginRight: "0.2em" }} />}
      {classification === "expired" ? (
        <Box>Expired ({formatted})</Box>
      ) : (
        dateWithOverrideHint
      )}
    </Box>
  );
}

export function PatientListRow({
  row,
  index,
  isRemoving,
  onRemove,
}: RemovalControl & {
  row: AvailablePatientListRow;
  index: number;
}) {
  const theme = useTheme();
  const navigate = useNavigate();

  // The substantive detention leads; a Section 136 sits under it.
  const states = [...row.states].sort((a, b) => {
    const a136 = a.state.mhaStatus.status === MhaStatus.Section136 ? 1 : 0;
    const b136 = b.state.mhaStatus.status === MhaStatus.Section136 ? 1 : 0;
    return a136 - b136;
  });
  const noStatus = states.length === 0 ? "—" : null;

  const isDeceasedPatient = ["formal", "informal"].includes(
    row.patientInfo.deathNotificationStatus ?? "",
  );

  const stackSx = { display: "flex", flexDirection: "column", gap: 1 } as const;

  return (
    <TableRow
      sx={{
        backgroundColor:
          index % 2 === 0 ? theme.palette.common.paleBlue : "white",
      }}
    >
      <RemoveFromListCell
        patientName={`${row.patientInfo.name.given} ${row.patientInfo.name.family}`}
        isRemoving={isRemoving}
        onRemove={onRemove}
      />
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Link
            style={{ textDecoration: "none" }}
            to={routeFns.patientHome(row.rootPatientId)}
          >
            <Typography
              variant="subtitle1"
              sx={{ fontSize: "0.9375rem", color: "blue" }}
            >
              {`${row.patientInfo.name.family.toUpperCase()}, ${row.patientInfo.name.given}`}
            </Typography>
          </Link>
          {isDeceasedPatient && (
            <TooltipInfoBox
              title="This patient has been marked as deceased in NHS national systems"
              icon={<Info sx={{ fontSize: "1.5rem", ml: 1 }} />}
            />
          )}
        </Box>
      </TableCell>
      <TableCell>
        {row.patientInfo.dateOfBirth
          ? ukLocalFormatDate(row.patientInfo.dateOfBirth)
          : "Unknown"}
      </TableCell>
      <TableCell>
        {NhsNumberSchema.catch("Unknown").parse(row.patientInfo.nhsNumber)}
      </TableCell>
      <TableCell>
        <Box sx={stackSx}>
          {noStatus}
          {states.map((s) => (
            <Box key={s.id}>{hospitalOrPosName(s)}</Box>
          ))}
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={stackSx}>
          {noStatus}
          {states.map((s) => {
            const activeState = getActiveMhaStatus(
              s.state,
              dayjs().toISOString(),
            );
            return (
              <Box key={s.id}>
                {formatMhaStatus(activeState.state, activeState.type)}
              </Box>
            );
          })}
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={stackSx}>
          {noStatus}
          {states.map((s) => (
            <Box key={s.id}>
              <ExpiryCell state={s} />
            </Box>
          ))}
        </Box>
      </TableCell>
      <TableCell align="right">
        {/* A link may not contain a button, so View navigates rather than
            wrapping in a Link. */}
        <Button
          label="View"
          onClick={() => {
            navigate(routeFns.patientHome(row.rootPatientId));
          }}
        />
      </TableCell>
    </TableRow>
  );
}

export function UnavailablePatientListRow({
  index,
  isRemoving,
  onRemove,
}: RemovalControl & { index: number }) {
  const theme = useTheme();

  return (
    <TableRow
      sx={{
        backgroundColor:
          index % 2 === 0 ? theme.palette.common.paleBlue : "white",
        color: theme.palette.text.disabled,
      }}
    >
      <RemoveFromListCell
        patientName={null}
        isRemoving={isRemoving}
        onRemove={onRemove}
      />
      <TableCell sx={{ color: theme.palette.text.disabled }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          Patient unavailable
          <TooltipInfoBox
            title="You no longer have access to this patient. They remain on your list in case access is restored."
            icon={<Info sx={{ fontSize: "1.25rem", ml: 1 }} />}
          />
        </Box>
      </TableCell>
      <TableCell sx={{ color: theme.palette.text.disabled }}>
        {"\u2014"}
      </TableCell>
      <TableCell sx={{ color: theme.palette.text.disabled }}>
        {"\u2014"}
      </TableCell>
      <TableCell sx={{ color: theme.palette.text.disabled }}>
        {"\u2014"}
      </TableCell>
      <TableCell sx={{ color: theme.palette.text.disabled }}>
        {"\u2014"}
      </TableCell>
      <TableCell sx={{ color: theme.palette.text.disabled }}>
        {"\u2014"}
      </TableCell>
      <TableCell />
    </TableRow>
  );
}

export function PatientList({
  entries,
  total,
  page,
  rowsPerPage,
  isLoading,
  error,
  sort,
  onSortChange,
  searchTerm,
  onSearchChange,
  onClearFilters,
  availableMhaStatuses,
  selectedMhaStatuses,
  onMhaStatusesChange,
  availableLocationNames,
  selectedLocationNames,
  onLocationNamesChange,
  onPageChange,
  onAddPatient,
  onRemovePatient,
}: PatientListProps) {
  const [confirmingRemovalOf, setConfirmingRemovalOf] = useState<string | null>(
    null,
  );
  const [removing, setRemoving] = useState<ReadonlySet<string>>(new Set());

  const removes = async (rootPatientId: string) => {
    setConfirmingRemovalOf(null);
    setRemoving((current) => new Set([...current, rootPatientId]));
    try {
      await onRemovePatient(rootPatientId);
    } finally {
      setRemoving(
        (current) =>
          new Set([...current].filter((pending) => pending !== rootPatientId)),
      );
    }
  };

  const removalControlFor = (rootPatientId: string): RemovalControl => ({
    isRemoving: removing.has(rootPatientId),
    onRemove: () => {
      setConfirmingRemovalOf(rootPatientId);
    },
  });

  const isSorted =
    sort.sortBy !== defaultPatientListSort.sortBy ||
    sort.sortDirection !== defaultPatientListSort.sortDirection;
  const isFiltered =
    selectedMhaStatuses.length > 0 || selectedLocationNames.length > 0;

  const stillSelectableWhenNoLongerOffered = <T extends string>(
    available: T[],
    chosen: T[],
  ): T[] => [...available, ...chosen.filter((one) => !available.includes(one))];

  const mhaStatusOptions: FilterOption[] = stillSelectableWhenNoLongerOffered(
    availableMhaStatuses,
    selectedMhaStatuses,
  )
    .map((status) => ({
      value: status,
      label: formatMhaStatus(status, "active"),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const locationOptions: FilterOption[] = stillSelectableWhenNoLongerOffered(
    availableLocationNames,
    selectedLocationNames,
  ).map((name) => ({ value: name, label: name }));

  const search = (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: "1em",
      }}
    >
      <PatientSearchField value={searchTerm} onChange={onSearchChange} />
      {(isSorted || isFiltered) && (
        <Button
          label="Clear filters"
          onClick={onClearFilters}
          testId="clear-filters"
        />
      )}
    </Box>
  );

  if (error !== undefined) {
    return (
      <Box>
        {search}
        <Banner bannerType={BannerList.ERROR} title={error} />
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box>
        {search}
        <LoadingSpinner />
      </Box>
    );
  }

  const firstOnPage = total === 0 ? 0 : page * rowsPerPage + 1;
  const lastOnPage = Math.min(total, page * rowsPerPage + entries.length);

  let body: ReactNode;
  if (entries.length === 0) {
    body = (
      <TableRow>
        <TableCell colSpan={COLUMN_COUNT}>
          <Typography sx={{ padding: "1em 0" }}>
            {searchTerm.trim() === "" && !isFiltered
              ? "Search for patients to add to patient list"
              : "No patients on your list match what you are looking for"}
          </Typography>
        </TableCell>
      </TableRow>
    );
  } else {
    body = entries.map((entry, index) =>
      entry.kind === "available" ? (
        <PatientListRow
          key={entry.row.rootPatientId}
          row={entry.row}
          index={index}
          {...removalControlFor(entry.row.rootPatientId)}
        />
      ) : (
        <UnavailablePatientListRow
          key={entry.rootPatientId}
          index={index}
          {...removalControlFor(entry.rootPatientId)}
        />
      ),
    );
  }

  return (
    <Box>
      {search}
      {confirmingRemovalOf !== null && (
        <ConfirmationModal
          message="Are you sure you want to remove this patient from your patient list? You can add them again using Add patient."
          confirmFn={() => {
            void removes(confirmingRemovalOf);
          }}
          closeFn={() => {
            setConfirmingRemovalOf(null);
          }}
        />
      )}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <SortableColumnHeader
                label="Name"
                column="name"
                options={[
                  { direction: "asc", label: "A → Z", badge: "A→Z" },
                  { direction: "desc", label: "Z → A", badge: "Z→A" },
                ]}
                sort={sort}
                onSortChange={onSortChange}
              />
              <SortableColumnHeader
                label="DOB"
                column="dateOfBirth"
                options={[
                  { direction: "asc", label: "Oldest first", badge: "Oldest" },
                  {
                    direction: "desc",
                    label: "Youngest first",
                    badge: "Youngest",
                  },
                ]}
                sort={sort}
                onSortChange={onSortChange}
              />
              <TableCell sx={{ fontWeight: 700 }}>NHS Number</TableCell>
              <FilterableColumnHeader
                label="Hospital / Place of Safety"
                options={locationOptions}
                selected={selectedLocationNames}
                onSelectionChange={onLocationNamesChange}
              />
              <FilterableColumnHeader
                label="MHA Status"
                options={mhaStatusOptions}
                selected={selectedMhaStatuses}
                onSelectionChange={(statuses) => {
                  onMhaStatusesChange(statuses as MhaStatus[]);
                }}
              />
              <SortableColumnHeader
                label="Expiry"
                column="expiry"
                options={[
                  {
                    direction: "asc",
                    label: "Soonest first",
                    badge: "Soonest",
                  },
                  { direction: "desc", label: "Latest first", badge: "Latest" },
                ]}
                sort={sort}
                onSortChange={onSortChange}
              />
              <TableCell align="right">
                <Button
                  label="Add patient"
                  endIcon={ButtonIcon.add}
                  outlined
                  onClick={onAddPatient}
                  testId="add-patient"
                />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{body}</TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          marginTop: "1em",
        }}
      >
        <Typography sx={{ fontSize: "0.875rem" }}>
          {total === 0
            ? "No patients"
            : `Showing ${String(firstOnPage)}–${String(lastOnPage)} of ${String(total)} patient${total === 1 ? "" : "s"}`}
        </Typography>
        <PaginationControls
          numPages={Math.max(1, Math.ceil(total / rowsPerPage))}
          page={page}
          setPage={onPageChange}
        />
      </Box>
    </Box>
  );
}
